import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SubscriptionService } from 'src/app/core/services/subscription.service';
import { PlanDefinition, BillingCycle } from 'src/app/core/models/subscription.model';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-pricing',
  templateUrl: './pricing.component.html',
  styleUrls: ['./pricing.component.css']
})
export class PricingComponent implements OnInit {

  plans: PlanDefinition[] = [];
  loading = true;
  processingPlan: string | null = null;
  billingCycle = BillingCycle.Monthly;
  BillingCycle = BillingCycle;

  currentPlan = '';
  isLoggedIn = false;

  // Set when the user arrives here via an expired subscription redirect.
  isExpiredRedirect = false;
  expiredPlanName = '';
  isUpgradeRedirect = false;
  requiredFeatureName = '';

  constructor(
    private subscriptionService: SubscriptionService,
    private route: ActivatedRoute,
    private router: Router
  ) { }

  ngOnInit(): void {
    // Read query params to know why the user is on this page.
    const params = this.route.snapshot.queryParamMap;

    this.isExpiredRedirect = params.get('expired') === 'true';
    this.expiredPlanName = params.get('plan') ?? '';
    this.isUpgradeRedirect = params.get('upgrade') === 'true';
    this.requiredFeatureName = params.get('feature') ?? '';

    // ✅ FIXED — always fetch fresh subscription data from the API instead
    // of reading a possibly-empty cached BehaviorSubject via getCurrent().
    // Previously, landing on /pricing before anything else had called
    // loadCurrent() meant currentPlan silently stayed blank forever,
    // even for a logged-in user on a paid plan — which is why "Current
    // Plan" never appeared on any card after upgrading.
    this.subscriptionService.loadCurrent().subscribe({
      next: (sub) => {
        this.currentPlan = sub.plan;
        this.isLoggedIn  = true;
      },
      error: () => {
        // Not logged in, or the request failed — leave isLoggedIn false
        // so the anonymous trial banner / register CTA show correctly.
        this.isLoggedIn = false;
      }
    });

    this.loadPlans();

    // Show a contextual alert if the user was redirected here
    if (this.isExpiredRedirect)
      this.showExpiredAlert();
  }

  private showExpiredAlert(): void {
    Swal.fire({
      title: 'Subscription Expired',
      html: `Your <strong>${this.expiredPlanName}</strong> subscription has expired. ` +
        `Renew or upgrade to restore full access.`,
      icon: 'warning',
      confirmButtonText: 'View Plans',
      confirmButtonColor: '#2BB0A6',
      allowOutsideClick: true
    });
  }

  loadPlans(): void {
    this.loading = true;
    this.subscriptionService.getPlans().subscribe({
      next: (plans) => {
        this.plans = plans;
        this.loading = false;
      },
      error: () => { this.loading = false; }
    });
  }

  selectPlan(plan: PlanDefinition): void {
    if (this.isCurrentPlan(plan.plan)) return;
    if (plan.plan === 'Free') return;

    this.processingPlan = plan.plan;

    // ✅ FIXED — build the real callback URL and send it to the backend
    // so Paystack has a concrete address to redirect back to after payment.
    const callbackUrl = `${window.location.origin}/payment/success`;

    this.subscriptionService
      .initializePayment(plan.plan, this.billingCycle, callbackUrl)
      .subscribe({
        next: (res) => {
          window.location.href = res.paymentUrl;
        },
        error: (err) => {
          this.processingPlan = null;
          Swal.fire({
            title: 'Payment Error',
            text: err?.error?.message ?? 'Could not initiate payment. Please try again.',
            icon: 'error',
            confirmButtonColor: '#2BB0A6'
          });
        }
      });
  }

  isCurrentPlan(plan: string): boolean {
    return this.currentPlan?.toLowerCase() === plan?.toLowerCase();
  }

  isPopular(plan: string): boolean {
    return plan === 'Professional';
  }

  getMonthlyEquivalent(plan: PlanDefinition): number {
    return this.billingCycle === BillingCycle.Annual
      ? Math.round(plan.priceAnnual / 12)
      : plan.priceMonthly;
  }

  toggleBillingCycle(): void {
    this.billingCycle = this.billingCycle === BillingCycle.Monthly
      ? BillingCycle.Annual
      : BillingCycle.Monthly;
  }
}